import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
} from "@capacitor/push-notifications";
import { useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { analytics, AnalyticsEvents } from "common/analytics";
import { AddNewEventButton } from "common/components";
import {
  FarmIcon,
  FilesIcon,
  InitialScreenSelectLogo,
  InsightsIcon,
  RecordsIcon,
} from "common/icons";
import ProfileIconSvg from "common/icons/ProfileIcon";
import ProgrammesIcon from "common/icons/ProgrammesIcon";
import { useTranslation } from "common/locales";
import { OnboardingChecklist } from "features/onboarding/OnboardingChecklist";
import ProfileDialog from "features/userProfile/Profile";
import { getAllFieldGeomsFromFields } from "model/fieldHelpers";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { useCurrentFarm } from "model/useFarmQueries";
import { useFields } from "model/useFieldQueries";
import { useEffect, useMemo, useState } from "react";
import type { FeatureCollection } from "geojson";
import { useLocation, useNavigate } from "react-router-dom";
import { zoomToGeom } from "services";
import { useAppStore, useUIStore } from "stores";

const InitialSelectScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: currentFarm } = useCurrentFarm();
  const currentFarmId = currentFarm?.guid;
  const { data: fields = [] } = useFields();
  const fieldGeoms = useMemo(
    () => getAllFieldGeomsFromFields(fields),
    [fields]
  );
  const user = useAppStore((state) => state.user);
  const openPushNotificationModal = useUIStore(
    (state) => state.openPushNotificationModal
  );

  // Profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const setupPushNotifications = () => {
    // Only setup push notifications on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    // Push notification setup is now handled in the device registration utility
    // This function is kept for backward compatibility but the actual setup
    // happens during app initialization and login/signup flows

    // Set up push notification received listener
    void PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotificationSchema) => {
        console.log("Push received: " + JSON.stringify(notification));
        handlePushNotification(notification);
      }
    );

    void PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification: ActionPerformed) => {
        console.log("Push action performed: " + JSON.stringify(notification));
        // Handle when user opens the app from the push notification
        if (notification.notification?.data) {
          handlePushNotification(notification.notification);
        }
      }
    );
  };

  const handlePushNotification = (notification: PushNotificationSchema) => {
    if (notification.data?.type === "file_entry") {
      // Handle file sharing notification
      const fileName = notification.data.fileName;
      const uploaderName = notification.data.uploaderName;

      if (fileName && uploaderName) {
        openPushNotificationModal({ fileName, uploaderName });
      }
    }
  };

  const handleProfileDialogOpen = () => {
    setProfileDialogOpen(true);
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
  };

  useEffect(() => {
    if (fieldGeoms.features.length > 0) {
      zoomToGeom(fieldGeoms);
    } else {
      if (currentFarm?.geom) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        zoomToGeom(currentFarm.geom as FeatureCollection);
      }
    }
    // we need to disable lint here because we cannot have dependency on an object - it would keep zooming otherwise
    // Push notification listeners are set up elsewhere during auth/init flows.
    // This screen is always rendered in the desktop drawer, so it must not
    // independently trigger app/farm initialization (would duplicate requests).
    setupPushNotifications();
  }, [currentFarm?.guid]);

  const isCorporateUser = !!(
    user?.corporations?.length && user.corporations.length > 0
  );
  const { data: accessibleFarmerProgrammes } =
    useAccessibleFarmerProgrammes(isCorporateUser);
  const hasFarmerDetailAccess = (accessibleFarmerProgrammes?.length ?? 0) > 0;

  // Redirect to farm view on mobile since we have bottom tab navigation
  useEffect(() => {
    if (isMobile && currentFarmId) {
      navigate(`/farm/${currentFarmId}`, { replace: true });
    }
  }, [isMobile, currentFarmId, navigate]);

  // For web (desktop), show new Figma design
  const location = useLocation();
  const currentRoute = location.pathname;
  let profileText = "Profile";
  if (user?.name && !user?.name.includes("@")) {
    profileText = user.name;
  }

  return (
    <DrawerContainer>
      {/* Logo */}
      <LogoWrapper>
        <InitialScreenSelectLogo color="#2E7831" />
      </LogoWrapper>

      {/* Content Wrapper */}
      <ContentWrapper>
        {/* Navigation Buttons - matching mobile bottom navigation */}
        <NavButtonsContainer>
          {/* Farm tab */}
          {!isCorporateUser && (
            <NavButton
              active={
                currentRoute.includes("/farm/") ||
                currentRoute.includes("/field/") ||
                currentRoute.includes("/animal-group/") ||
                currentRoute.includes("/fields-list/")
              }
              onClick={() => {
                analytics.track(AnalyticsEvents.FARM_VIEW_OPENED);
                navigate(`/farm/${currentFarmId || ""}`);
              }}
            >
              <FarmIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.landmanagement.and.grazing.button")}</span>
            </NavButton>
          )}

          {/* Records tab */}
          {!isCorporateUser && (
            <NavButton
              active={currentRoute === "/records"}
              onClick={() => {
                analytics.track(AnalyticsEvents.RECORDS_OPENED);
                navigate(`/records`);
              }}
            >
              <RecordsIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.records")}</span>
            </NavButton>
          )}

          {/* Planner tab */}
          {!isCorporateUser && (
            <NavButton
              active={
                currentRoute === "/planner" ||
                currentRoute.includes("/programme-details/")
              }
              onClick={() => {
                analytics.track(AnalyticsEvents.PLANNER_OPENED);
                navigate(`/planner`);
              }}
            >
              <ProgrammesIcon
                sx={{ width: 20, height: 20, color: "inherit" }}
              />
              <span>{t("go.to.planner")}</span>
            </NavButton>
          )}

          {/* Insights tab */}
          {!isCorporateUser && (
            <NavButton
              active={currentRoute === "/farm-insights"}
              onClick={() => {
                analytics.track(AnalyticsEvents.FARM_INSIGHTS_OPENED);
                navigate(`/farm-insights`);
              }}
            >
              <InsightsIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.farm.insights")}</span>
            </NavButton>
          )}

          {/* Corporate user tabs */}
          {isCorporateUser && (
            <NavButton
              active={currentRoute === "/programmes"}
              onClick={() => {
                analytics.track(AnalyticsEvents.PROJECT_INSIGHTS_OPENED);
                navigate(`/programmes`);
              }}
            >
              <ProgrammesIcon
                sx={{ width: 20, height: 20, color: "inherit" }}
              />
              <span>{t("go.to.project.insights")}</span>
            </NavButton>
          )}

          {isCorporateUser && hasFarmerDetailAccess && (
            <NavButton
              active={currentRoute === "/farmers"}
              onClick={() => {
                analytics.track(
                  AnalyticsEvents.ACCESSIBLE_FARMS_INSIGHTS_OPENED
                );
                navigate(`/farmers`);
              }}
            >
              <InsightsIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.accessible.farms.insights")}</span>
            </NavButton>
          )}

          {isCorporateUser && (
            <NavButton
              active={currentRoute === "/files"}
              onClick={() => {
                analytics.track("Go to file management view clicked");
                navigate(`/files`);
              }}
            >
              <FilesIcon sx={{ width: 20, height: 20, color: "inherit" }} />
              <span>{t("go.to.file.management.button")}</span>
            </NavButton>
          )}

          {/* Add New Event Button */}
          {!isCorporateUser && (
            <div style={{ marginTop: "16px", width: "100%" }}>
              <AddNewEventButton maxWidth="100%" />
            </div>
          )}
        </NavButtonsContainer>
      </ContentWrapper>

      {/* Onboarding checklist docked above the profile (farmers only) */}
      {!isCorporateUser && (
        <OnboardingWrapper>
          <OnboardingChecklist variant="sidebar" />
        </OnboardingWrapper>
      )}

      {/* Profile at bottom */}
      <ProfileWrapper>
        <ProfileCard
          onClick={handleProfileDialogOpen}
          aria-label="Open profile settings"
        >
          <ProfileIconSvg
            fontSize="medium"
            sx={{ color: "inherit", flexShrink: 0 }}
          />
          <ProfileName>{profileText}</ProfileName>
        </ProfileCard>
      </ProfileWrapper>

      {/* Profile Dialog */}
      <ProfileDialog
        open={profileDialogOpen}
        onClose={handleProfileDialogClose}
      />
    </DrawerContainer>
  );
};

// Styled Components for the new Figma design
const DrawerContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  background: "#f1eff7",
  padding: "16px",
});

const LogoWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "24px",
});

const ContentWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
});

const NavButtonsContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
});

interface NavButtonProps {
  active: boolean;
}

const NavButton = styled("button")<NavButtonProps>(({ active }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 12px",
  borderRadius: "10px",
  backgroundColor: active ? "#d3c9e7" : "transparent",
  border: "none",
  cursor: "pointer",
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: 1.3,
  color: active ? "#433765" : "#7D5DA3",
  textAlign: "left",
  transition: "background-color 0.2s, color 0.2s, transform 0.15s",
  width: "100%",
  "&:hover": {
    backgroundColor: active ? "#d3c9e7" : "#e0daee",
    color: "#433765",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  "& span": {
    flex: 1,
    minWidth: 0,
  },
  "& svg": {
    color: `${active ? "#433765" : "#7D5DA3"} !important`,
  },
  "&:hover svg": {
    color: "#433765 !important",
  },
}));

const OnboardingWrapper = styled("div")({
  width: "100%",
  paddingTop: "16px",
});

const ProfileWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 0,
  paddingTop: "16px",
});

const ProfileCard = styled("button")({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 12px",
  borderRadius: "10px",
  backgroundColor: "transparent",
  border: "none",
  cursor: "pointer",
  width: "100%",
  minWidth: 0,
  color: "#7D5DA3",
  textAlign: "left",
  transition: "background-color 0.2s, color 0.2s, transform 0.15s",
  "&:hover": {
    backgroundColor: "#e0daee",
    color: "#433765",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
});

const ProfileName = styled("span")({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: 1.3,
  color: "inherit",
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
});

export default InitialSelectScreen;
